/**
 * uiLog.ts — Unified event bus for UI interactions.
 *
 * Every event is:
 *   1. Stored in _events[] for window.printStory() and downloadTrace()
 *   2. Emitted through Roarr (namespace="ui") for console visibility
 *
 * Event kinds:
 *   screen-render  — a new screen (or sub-screen) mounted with its content props
 *   click          — a user tap on an interactive element
 *   engine         — engine status transition (IDLE → RUNNING etc.)
 *   ui-action      — UIAction dispatched through dispatchUI
 *   engine-log     — INFO+ Roarr message from the engine internals
 *   marker         — manually inserted annotation (window.markStory)
 */

import { Roarr } from "roarr";

export type EventKind =
  | "screen-render"
  | "click"
  | "engine"
  | "ui-action"
  | "engine-log"
  | "marker";

export type StoryEntry = {
  ts: number;
  iso: string;
  kind: EventKind;
  screen: string;
  label: string;
  detail?: Record<string, unknown>;
};

let _currentScreen = "booting";
const _events: StoryEntry[] = [];

// Roarr child logger — all UI events share namespace="ui"
const _uiLog = Roarr.child({ namespace: "ui", logLevel: 30 });

function record(
  kind: EventKind,
  label: string,
  detail?: Record<string, unknown>,
): void {
  const now = Date.now();
  const entry: StoryEntry = {
    ts: now,
    iso: new Date(now).toISOString(),
    kind,
    screen: _currentScreen,
    label,
    detail,
  };
  _events.push(entry);
  // Emit through Roarr for console visibility (but NOT engine-log kind to avoid duplicates)
  if (kind !== "engine-log") {
    _uiLog.info({ kind, screen: _currentScreen, ...detail }, label);
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Called from RetreatBridge when the derived screen changes. */
export function logScreenRender(
  screen: string,
  detail?: Record<string, unknown>,
): void {
  _currentScreen = screen;
  record("screen-render", `RENDER: ${screen}`, detail);
}

/** Called from the DOM click capture listener in main.tsx. */
export function logClick(label: string): void {
  record("click", `TAP: ${label}`);
}

/** Called from the engine.subscribe() listener in main.tsx. */
export function logEngineTransition(from: string, to: string): void {
  record("engine", `ENGINE: ${from} -> ${to}`);
}

/** Called from the Roarr.write interception in main.tsx. */
export function logRoarrEntry(
  message: string,
  context?: Record<string, unknown>,
): void {
  // Message already includes [namespace] prefix from Roarr, don't add it again
  const detail = context ? ({ ...context } as Record<string, unknown>) : undefined;
  record("engine-log", message, detail);
}

/** Called from dispatchUI wrapper in RetreatBridge. */
export function logUIAction(actionType: string, detail?: Record<string, unknown>): void {
  record("ui-action", actionType, detail);
}

/** Insert a named annotation. Usage: window.markStory("confusion starts here") */
export function addStoryMarker(label: string): void {
  record("marker", `MARK: ${label}`);
}

/** Remove the event at zero-based index. Usage: window.removeStoryEntry(5) */
export function removeStoryEntry(index: number): void {
  if (index >= 0 && index < _events.length) {
    _events.splice(index, 1);
  }
}

export function getStory(): StoryEntry[] {
  return [..._events];
}

export function clearUILog(): void {
  _events.length = 0;
  _currentScreen = "booting";
}

/** Serialize the current event story to a File for attaching to a bug report. */
export function dumpLogsAsFile(): File {
  const payload = {
    dumpedAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    events: _events,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  return new File([blob], `rc-trace-${Date.now()}.json`, { type: "application/json" });
}

